import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import {
  Perm,
  type PurchaseDetail,
  type Receipt,
  type ReceiptDetail,
  type Supplier,
  type Warehouse,
} from '../api/types'
import { formatMoney, formatMoment, formatQty, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Row, Select, Spinner, TextInput,
} from '@shop/ui'
import { allocationBases, receiptStatusNames } from '../ui/purchasing'

/**
 * إيصالاتُ الاستلام.
 *
 * <b>وترحيلُ الإيصال هو الحدثُ الذي يزيد المخزون</b> — لا اعتمادُ أمر الشراء.
 * ولذلك صلاحيّتُه مستقلّة، والشاشةُ تفصل الكتابةَ عن الترحيل كما يفصلهما الخادم.
 */
export default function Receipts() {
  const { can } = useAuth()
  const [status, setStatus] = useState('')

  const receipts = useQuery({
    queryKey: ['receipts', status],
    queryFn: () => api<Receipt[]>(`/api/v1/receipts?status=${status}`),
  })

  return (
    <Panel
      title="إيصالاتُ الاستلام"
      actions={can(Perm.receiptCreate) && <NewReceiptButton />}
    >
      <div className="mb-4">
        <Field label="الحالة">
          <Select value={status} onChange={setStatus}>
            <option value="">الكلّ</option>
            <option value="DRAFT">مسوّدة</option>
            <option value="POSTED">مُرحَّل</option>
            <option value="REVERSED">معكوس</option>
          </Select>
        </Field>
      </div>

      {receipts.isPending && <Spinner />}
      {receipts.isError && <ErrorBanner error={receipts.error} />}
      {receipts.data?.length === 0 && <Empty>لا إيصالاتٍ مطابقة.</Empty>}

      <ul className="space-y-2">
        {(receipts.data ?? []).map((receipt) => (
          <li key={receipt.id}>
            <Link
              to={`/receipts/${receipt.id}`}
              className="block rounded-md border border-line px-3 py-2 hover:bg-surface"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{receipt.supplierName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{receipt.receiptNo}</span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span>{receiptStatusNames[receipt.status] ?? receipt.status}</span>
                <span className="tabular">{formatQty(receipt.totalQty)} قطعة</span>

                {/* ⚠ استلامٌ بلا أمرٍ يُرى باسمه لا يُخفى — فيُقرَّر بعددٍ لا بانطباع */}
                {receipt.purchaseNo ? (
                  <span className="tabular" dir="ltr">{receipt.purchaseNo}</span>
                ) : (
                  <span className="text-warn">بلا أمرِ شراء</span>
                )}

                <span className="tabular">{receipt.receiptDate}</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

function NewReceiptButton() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const [supplierId, setSupplierId] = useState('')
  const [warehouseId, setWarehouseId] = useState('')
  const [purchaseId, setPurchaseId] = useState('')
  const [invoiceNo, setInvoiceNo] = useState('')
  const [otherCosts, setOtherCosts] = useState('')
  const [basis, setBasis] = useState('BY_VALUE')

  const suppliers = useQuery({
    queryKey: ['suppliers', '', false],
    queryFn: () => api<Supplier[]>('/api/v1/suppliers'),
    enabled: open,
  })

  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api<Warehouse[]>('/api/v1/warehouses'),
    enabled: open,
  })

  // أوامرُ هذا المورّد المفتوحة — والاستلامُ عليها أو بلا أمرٍ أصلًا.
  const purchases = useQuery({
    queryKey: ['purchases', 'CONFIRMED', supplierId],
    queryFn: () => api<{ id: string; purchaseNo: string }[]>(
      `/api/v1/purchases?status=CONFIRMED&supplierId=${supplierId}`),
    enabled: open && supplierId.length > 0,
  })

  const create = useMutation({
    mutationFn: () =>
      api<Receipt>('/api/v1/receipts', {
        method: 'POST',
        body: {
          supplierId,
          warehouseId,
          purchaseId: purchaseId || null,
          supplierInvoiceNo: invoiceNo || null,
          otherCosts: toNumber(otherCosts) ?? 0,
          allocationBasis: basis,
        },
      }),
    onSuccess: async (receipt) => {
      await queryClient.invalidateQueries({ queryKey: ['receipts'] })
      setOpen(false)
      void navigate(`/receipts/${receipt.id}`)
    },
  })

  if (!open) {
    return <Button tone="primary" onClick={() => setOpen(true)}>استلامٌ جديد</Button>
  }

  return (
    <div className="w-full space-y-3 rounded-md border border-line bg-surface p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="المورّد">
          <Select value={supplierId} onChange={setSupplierId}>
            <option value="">اختر مورّدًا…</option>
            {(suppliers.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.nameAr}</option>
            ))}
          </Select>
        </Field>

        <Field label="المستودع" hint="والفرعُ يُشتقّ منه">
          <Select value={warehouseId} onChange={setWarehouseId}>
            <option value="">اختر مستودعًا…</option>
            {(warehouses.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>{w.nameAr}</option>
            ))}
          </Select>
        </Field>

        <Field label="أمرُ الشراء" hint="اتركه فارغًا للاستلام المباشر">
          <Select value={purchaseId} onChange={setPurchaseId} disabled={!supplierId}>
            <option value="">بلا أمرِ شراء</option>
            {(purchases.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.purchaseNo}</option>
            ))}
          </Select>
        </Field>

        <Field label="رقمُ فاتورة المورّد" hint="يُفحص تكرارُه عند الترحيل">
          <TextInput value={invoiceNo} onChange={setInvoiceNo} dir="ltr" />
        </Field>

        <Field label="مصاريفُ الشحنة" hint="شحنٌ وجمركٌ — تُوزَّع على السطور">
          <NumberInput value={otherCosts} onChange={setOtherCosts} placeholder="0" />
        </Field>

        <Field
          label="أساسُ التوزيع"
          hint={allocationBases.find((b) => b.code === basis)?.hint}
        >
          <Select value={basis} onChange={setBasis}>
            {allocationBases.map((b) => (
              <option key={b.code} value={b.code}>{b.label}</option>
            ))}
          </Select>
        </Field>
      </div>

      {create.isError && <ErrorBanner error={create.error} />}

      <div className="flex gap-2">
        <Button
          tone="primary"
          disabled={!supplierId || !warehouseId || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'يُنشأ…' : 'إنشاء'}
        </Button>

        <Button onClick={() => setOpen(false)}>إلغاء</Button>
      </div>
    </div>
  )
}

/** إيصالٌ بتفاصيله وسطوره — وزرُّ الترحيل إن مُلكت صلاحيّتُه. */
export function ReceiptDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()

  const receipt = useQuery({
    queryKey: ['receipt', id],
    queryFn: () => api<ReceiptDetail>(`/api/v1/receipts/${id}`),
    enabled: Boolean(id),
  })

  if (receipt.isPending) {
    return <Spinner />
  }

  if (receipt.isError) {
    return <ErrorBanner error={receipt.error} />
  }

  const { header, lines } = receipt.data!
  const isDraft = header.status === 'DRAFT'

  return (
    <div className="space-y-4">
      <Panel title={`إيصالُ استلام ${header.receiptNo}`}>
        <dl className="space-y-1 text-sm">
          <Row label="المورّد">{header.supplierName}</Row>
          <Row label="الحالة">{receiptStatusNames[header.status] ?? header.status}</Row>

          <Row label="أمرُ الشراء">
            {header.purchaseNo ? (
              <Link className="underline underline-offset-4" to={`/purchases/${header.purchaseId}`}>
                {header.purchaseNo}
              </Link>
            ) : (
              'استلامٌ مباشرٌ بلا أمر'
            )}
          </Row>

          <Row label="التاريخ">{header.receiptDate}</Row>

          {header.supplierInvoiceNo && (
            <Row label="فاتورةُ المورّد">
              {header.supplierInvoiceNo}
              {header.duplicateAcknowledged && (
                <span className="text-warn"> · أُقرّ بتكرارها</span>
              )}
            </Row>
          )}

          {can(Perm.costView) && (
            <>
              <Row label="الإجمالي">{formatMoney(header.netTotal, header.currency)}</Row>
              {header.otherCosts > 0 && (
                <Row label="مصاريفُ الشحنة">
                  {formatMoney(header.otherCosts, header.currency)}
                  {' · '}
                  {allocationBases.find((b) => b.code === header.allocationBasis)?.label}
                </Row>
              )}
            </>
          )}

          {header.postedAt && <Row label="رُحِّل">{formatMoment(header.postedAt)}</Row>}
        </dl>

        {isDraft && can(Perm.receiptPost) && lines.length > 0 && (
          <div className="mt-4">
            <PostControl receipt={header} />
          </div>
        )}

        {isDraft && !can(Perm.receiptPost) && (
          <div className="mt-4">
            <Banner tone="info">
              الإيصالُ مسوّدة. وترحيلُه — وهو ما يزيد المخزون — يحتاج صلاحيّةً
              مستقلّة، فمن يكتب لا يُرحّل.
            </Banner>
          </div>
        )}
      </Panel>

      <Panel title="السطور">
        {lines.length === 0 && <Empty>لا سطورَ بعد.</Empty>}

        <ul className="space-y-2">
          {lines.map((line) => (
            <li key={line.id} className="rounded-md border border-line px-3 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{line.productName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{line.sku}</span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span className="tabular">{formatQty(line.qty)}</span>

                {can(Perm.costView) && (
                  <>
                    <span className="tabular">{formatMoney(line.netAmount, header.currency)}</span>

                    {line.allocatedCost > 0 && (
                      <span className="tabular">
                        + شحنٌ {formatMoney(line.allocatedCost, header.currency)}
                      </span>
                    )}

                    {line.ledgerUnitCost !== null && (
                      <span className="tabular">
                        كلفةُ الدفتر {formatMoney(line.ledgerUnitCost)}
                      </span>
                    )}
                  </>
                )}

                {/* سطرٌ حرّ: وصل ما لم يُطلب — ويُرى بسببه لا يُبتلع */}
                {line.purchaseLineId === null && header.purchaseId !== null && (
                  <span className="text-warn">خارجَ الأمر · {line.freeLineReason}</span>
                )}
              </div>
            </li>
          ))}
        </ul>

        {isDraft && can(Perm.receiptCreate) && (
          <div className="mt-3">
            <ReceiptLineEditor receipt={header} lines={lines} />
          </div>
        )}
      </Panel>
    </div>
  )
}

/**
 * الترحيل — <b>وهنا يتحرّك المخزون</b>.
 *
 * وفاتورةٌ مكرّرةٌ تُردّ برسالةٍ تسمّي الإيصالَ الآخر. والإقرارُ بها بابٌ يُفتح
 * بصلاحيّةٍ وسببٍ مسجَّلين — <b>ولا تجاوزَ صامت</b>.
 */
function PostControl({ receipt }: { receipt: Receipt }) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [duplicate, setDuplicate] = useState<string | null>(null)

  const post = useMutation({
    mutationFn: (acknowledge: boolean) =>
      api<ReceiptDetail>(`/api/v1/receipts/${receipt.id}/post`, {
        method: 'POST',
        body: acknowledge
          ? { acknowledgeDuplicate: true, duplicateReason: reason }
          : {},
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['receipt', receipt.id] })
      await queryClient.invalidateQueries({ queryKey: ['receipts'] })
      await queryClient.invalidateQueries({ queryKey: ['purchases'] })
      setDuplicate(null)
    },
    onError: (error: unknown) => {
      const code = (error as { code?: string })?.code

      if (code === 'purch.duplicate_supplier_invoice') {
        setDuplicate((error as { message?: string })?.message ?? 'فاتورةٌ مكرّرة.')
      }
    },
  })

  if (duplicate !== null) {
    return (
      <div className="space-y-2">
        <Banner tone="danger">{duplicate}</Banner>

        {can(Perm.receiptAckDuplicate) ? (
          <>
            <Banner tone="warn">
              لو كانت فاتورةً أخرى بحقّ — كمورّدٍ يُصفّر ترقيمَه سنويًّا — فأقِرّ
              بذلك صراحةً. ويُسجَّل اسمُك وسببُك مع الإيصال.
            </Banner>

            <Field label="سببُ الإقرار" hint="عشرةُ أحرفٍ فأكثر">
              <TextInput value={reason} onChange={setReason} placeholder="المورّدُ يُصفّر ترقيمَه مع كلّ سنة" />
            </Field>

            <div className="flex gap-2">
              <Button
                tone="danger"
                disabled={reason.trim().length < 10 || post.isPending}
                onClick={() => post.mutate(true)}
              >
                {post.isPending ? 'يُرحَّل…' : 'أقِرُّ ورحِّل'}
              </Button>

              <Button onClick={() => setDuplicate(null)}>تراجع</Button>
            </div>
          </>
        ) : (
          <>
            <Banner tone="info">
              تجاوزُ هذا الحارس يحتاج صلاحيّةً مستقلّة. راجِع الإيصالَ الآخر، أو
              اطلب من المالك أن يُقرّ.
            </Banner>

            <Button onClick={() => setDuplicate(null)}>تراجع</Button>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Banner tone="warn">
        الترحيلُ يزيد المخزونَ ويثبّت الكلفة. <b>ولا يُعدَّل بعده</b> — يُصحَّح
        بعكسٍ لا بتعديل.
      </Banner>

      {post.isError && duplicate === null && <ErrorBanner error={post.error} />}

      <Button tone="primary" disabled={post.isPending} onClick={() => post.mutate(false)}>
        {post.isPending ? 'يُرحَّل…' : 'ترحيلُ الإيصال'}
      </Button>
    </div>
  )
}

type ReceiptLineRow = ReceiptDetail['lines'][number]

type DraftLine = {
  variantId: string
  label: string
  purchaseLineId: string | null
  qty: string
  cost: string
  reason: string
}

/**
 * تحريرُ سطور الإيصال.
 *
 * <b>وحين يكون للإيصال أمرٌ، تُعرض سطورُه بمتبقّيها</b> ويُملأ منها — فلا يبحث
 * الموظّفُ عن صنفٍ يعرف الأمرُ أنّه قادم. والزائدُ يُضاف سطرًا حرًّا بسببه.
 */
function ReceiptLineEditor({ receipt, lines }: { receipt: Receipt; lines: ReceiptLineRow[] }) {
  const queryClient = useQueryClient()

  const [draft, setDraft] = useState<DraftLine[]>(() =>
    lines.map((l) => ({
      variantId: l.variantId,
      label: `${l.productName} · ${l.sku}`,
      purchaseLineId: l.purchaseLineId,
      qty: l.qty.toString(),
      cost: l.unitCost.toString(),
      reason: l.freeLineReason ?? '',
    })),
  )

  const purchase = useQuery({
    queryKey: ['purchase', receipt.purchaseId],
    queryFn: () => api<PurchaseDetail>(`/api/v1/purchases/${receipt.purchaseId}`),
    enabled: receipt.purchaseId !== null,
  })

  const save = useMutation({
    mutationFn: () =>
      api<ReceiptDetail>(`/api/v1/receipts/${receipt.id}/lines`, {
        method: 'PUT',
        body: {
          lines: draft.map((d) => ({
            variantId: d.variantId,
            purchaseLineId: d.purchaseLineId,
            qty: toNumber(d.qty) ?? 0,
            unitCost: toNumber(d.cost) ?? 0,
            freeLineReason: d.reason || null,
          })),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['receipt', receipt.id] })
    },
  })

  const remaining = (purchase.data?.lines ?? []).filter(
    (line) => line.remainingQty > 0 && !draft.some((d) => d.purchaseLineId === line.id),
  )

  const incomplete = draft.some(
    (d) => (toNumber(d.qty) ?? 0) <= 0
      || (d.purchaseLineId === null && receipt.purchaseId !== null && d.reason.trim().length < 3),
  )

  return (
    <div className="space-y-3">
      {draft.map((line, index) => (
        <div key={`${line.variantId}-${index}`} className="rounded-md border border-line p-3">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium">{line.label}</span>
            <Button onClick={() => setDraft(draft.filter((_, i) => i !== index))}>حذف</Button>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="الكميّة">
              <NumberInput
                value={line.qty}
                onChange={(v) => setDraft(draft.map((d, i) => (i === index ? { ...d, qty: v } : d)))}
              />
            </Field>

            <Field label="كلفةُ الوحدة">
              <NumberInput
                value={line.cost}
                onChange={(v) => setDraft(draft.map((d, i) => (i === index ? { ...d, cost: v } : d)))}
              />
            </Field>
          </div>

          {line.purchaseLineId === null && receipt.purchaseId !== null && (
            <div className="mt-2">
              <Field label="سببُ السطر الحرّ" hint="وصل ما لم يُطلب — لماذا؟">
                <TextInput
                  value={line.reason}
                  onChange={(v) => setDraft(draft.map((d, i) => (i === index ? { ...d, reason: v } : d)))}
                  placeholder="هديّةُ المورّد"
                />
              </Field>
            </div>
          )}
        </div>
      ))}

      {remaining.length > 0 && (
        <div className="rounded-md border border-dashed border-line p-3">
          <p className="mb-2 text-xs text-ink-soft">باقٍ على الأمر — اضغط لتضيفه:</p>

          <ul className="space-y-1">
            {remaining.map((line) => (
              <li key={line.id}>
                <button
                  type="button"
                  className="w-full rounded-md px-2 py-1 text-start text-sm hover:bg-surface"
                  onClick={() =>
                    setDraft([...draft, {
                      variantId: line.variantId,
                      label: `${line.productName} · ${line.sku}`,
                      purchaseLineId: line.id,
                      qty: line.remainingQty.toString(),
                      cost: line.unitCost.toString(),
                      reason: '',
                    }])
                  }
                >
                  {line.productName}
                  <span className="tabular mx-2 text-xs text-ink-soft">
                    المتبقّي {formatQty(line.remainingQty)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {save.isError && <ErrorBanner error={save.error} />}

      <Button
        tone="primary"
        disabled={draft.length === 0 || incomplete || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'تُحفظ…' : 'حفظُ السطور'}
      </Button>
    </div>
  )
}
